import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Gamepad2, Tv, Shield, Wifi, Headphones } from "lucide-react";

const CONNECTED_HOME_IMAGE = "https://media.base44.com/images/public/69d90a760237c1a435afefa7/58e2b1b9e_generated_c107999d.png";

const allHighlights = [
  { icon: Zap, title: "Velocidade Máxima", desc: "Até 1 Gbps de download com fibra óptica real" },
  { icon: Gamepad2, title: "Ideal para Jogos", desc: "Latência mínima para gamers competitivos" },
  { icon: Tv, title: "Streaming sem Buffer", desc: "4K sem travamentos em todas as plataformas" },
  { icon: Shield, title: "Conexão Segura", desc: "Proteção avançada contra ameaças digitais" },
  { icon: Wifi, title: "Cobertura Estável", desc: "Sinal forte em toda a sua casa" },
  { icon: Headphones, title: "Suporte Rápido", desc: "Atendimento técnico sempre que precisar" },
];

export default function HighlightsCarousel() {
  const [current, setCurrent] = useState(0);
  // Filtra os cards que você deseja desativar
  const highlights = allHighlights.filter(
    (h) =>
      h.title !== "Suporte 24h" &&
      h.title !== "Cobertura Regional"
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((p) => (p + 1) % highlights.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={CONNECTED_HOME_IMAGE}
          alt="Casa conectada"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-foreground mb-4">
            Por que escolher a <span className="text-primary">WebNorte</span>?
          </h2>
          <p className="text-muted-foreground text-lg">
            Diferenciais que fazem a diferença no seu dia a dia
          </p>
        </motion.div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-6 hover:border-glow transition-all duration-300 group cursor-default"
              >
                <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center mb-4 group-hover:animate-float">
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-lg text-foreground mb-2">{h.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
              className="glass-card rounded-2xl p-8 text-center"
            >
              {(() => {
                const Icon = highlights[current].icon;
                return (
                  <>
                    <div className="h-16 w-16 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-poppins font-semibold text-xl text-foreground mb-2">
                      {highlights[current].title}
                    </h3>
                    <p className="text-muted-foreground">{highlights[current].desc}</p>
                  </>
                );
              })()}
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-center gap-2 mt-6">
            {highlights.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all duration-300 ${i === current ? "w-8 bg-primary" : "w-2 bg-muted-foreground/30"
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}